import {
  Box,
  Flex,
  Grid,
  HStack,
  Heading,
  Spinner,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  type CSSProperties,
  Fragment,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import {
  FiClock,
  FiCpu,
  FiEdit,
  FiGrid,
  FiInfo,
  FiList,
  FiMapPin,
  FiPlus,
  FiRotateCcw,
  FiSearch,
  FiTag,
  FiTrash2,
  FiWifi,
  FiX,
} from "react-icons/fi"

import { OpenAPI, ReadersService, TagsService } from "@/client"
import type { ApiError, TagRead, TagStateEnum, TagTypeEnum } from "@/client"
import ConfirmDialog from "@/components/Common/ConfirmDialog"
import {
  TimezoneSelector,
  formatInTimezone,
  resolveStoredTimezone,
} from "@/components/Common/TimezoneSelector"
import {
  IconBox,
  VisionButton,
  VisionCard,
  VisionInput,
  VisionSelect,
} from "@/components/VisionUI"
import { Badge } from "@/components/ui/badge"
import { InputGroup } from "@/components/ui/input-group"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination"
import { OrganizationContext, isAllOrgs } from "@/contexts/OrganizationContext"
import useAuth from "@/hooks/useAuth"
import useCustomToast from "@/hooks/useCustomToast"
import { useDemoMode } from "@/hooks/useDemoMode"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/tags")({
  component: Tags,
})

const getTagTypeIcon = (type: TagTypeEnum) => {
  switch (type) {
    case "TEMPERATURE":
      return "🌡️"
    case "HUMIDITY":
      return "💧"
    case "GENERIC":
      return "📡"
    default:
      return "📡"
  }
}

const getTagTypeColor = (type: TagTypeEnum) => {
  switch (type) {
    case "TEMPERATURE":
      return "red"
    case "HUMIDITY":
      return "blue"
    case "GENERIC":
      return "gray"
    default:
      return "gray"
  }
}

// Timezone list moved to @/components/Common/TimezoneSelector for reuse

function Tags() {
  const { t } = useTranslation(["tags", "common"])
  const sc = useSemanticColors()
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  // Empty-trash hits a DELETE that the backend gates with
  // ``forbid_in_demo_mode_unless_superuser``; the 4xx comes back with
  // no CORS headers so the browser reports it as a CORS error. Surface
  // the real reason here instead so demo visitors see a clear message.
  const { enabled: demoEnabled } = useDemoMode()
  const { user } = useAuth()
  const demoBlocksEmptyTrash = demoEnabled && !user?.is_superuser
  const navigate = useNavigate()
  const { currentOrganization, organizations } = useContext(OrganizationContext)
  const [tagSearch, setTagSearch] = useState("")
  const [tagViewMode, setTagViewMode] = useState<"grid" | "table">(
    () =>
      (localStorage.getItem("tags-view-mode") as "grid" | "table") || "grid",
  )
  // Honor the user's chosen view on all widths; the table view scrolls
  // horizontally on narrow screens so card<->detailed switching works on mobile.
  const effectiveViewMode: "grid" | "table" = tagViewMode
  const updateTagViewMode = (mode: "grid" | "table") => {
    setTagViewMode(mode)
    localStorage.setItem("tags-view-mode", mode)
  }
  const [typeFilter, setTypeFilter] = useState("")
  const [stateFilter, setStateFilter] = useState("")
  const [readerFilter, setReaderFilter] = useState("")
  const [tagPage, setTagPage] = useState(1)
  const [tagPageSize, setTagPageSize] = useState<number>(
    () => Number(localStorage.getItem("tags-page-size")) || 50,
  )
  const [appliedTagSearch, setAppliedTagSearch] = useState("")
  const [sortBy, setSortBy] = useState("")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc")
  const [displayTimezone, setDisplayTimezone] = useState<string>(() =>
    resolveStoredTimezone(localStorage.getItem("tags-timezone")),
  )

  const formatDate = (dateStr: string | null | undefined): string =>
    formatInTimezone(dateStr, displayTimezone)

  const toggleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"))
    } else {
      setSortBy(column)
      setSortOrder("asc")
    }
    setTagPage(1)
  }

  const [colWidths, setColWidths] = useState<Record<string, number>>({})
  const resizingRef = useRef<{
    col: string
    startX: number
    startW: number
  } | null>(null)

  const startResize = useCallback(
    (col: string, e: ReactMouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      const th = (e.currentTarget as HTMLElement).closest("th") as HTMLElement
      const startW = th.getBoundingClientRect().width
      resizingRef.current = { col, startX: e.clientX, startW }
      const onMouseMove = (me: MouseEvent) => {
        if (!resizingRef.current) return
        const { col: c, startX, startW: sw } = resizingRef.current
        setColWidths((prev) => ({
          ...prev,
          [c]: Math.max(60, sw + me.clientX - startX),
        }))
      }
      const onMouseUp = () => {
        resizingRef.current = null
        document.removeEventListener("mousemove", onMouseMove)
        document.removeEventListener("mouseup", onMouseUp)
      }
      document.addEventListener("mousemove", onMouseMove)
      document.addEventListener("mouseup", onMouseUp)
    },
    [],
  )

  const rh = (col: string) => (
    <div
      onMouseDown={(e) => startResize(col, e)}
      style={{
        position: "absolute",
        right: 0,
        top: 0,
        bottom: 0,
        width: 5,
        cursor: "col-resize",
        zIndex: 1,
      }}
    />
  )

  const cw = (col: string, def: number): CSSProperties => ({
    position: "relative",
    width: colWidths[col] ?? def,
    maxWidth: colWidths[col] ?? def,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  })

  const applyTagFilter = () => {
    setAppliedTagSearch(tagSearch)
    setTagPage(1)
  }

  const clearTagFilter = () => {
    setTagSearch("")
    setAppliedTagSearch("")
    setTypeFilter("")
    setStateFilter("")
    setReaderFilter("")
    setTagPage(1)
  }
  const [tagToDelete, setTagToDelete] = useState<TagRead | null>(null)
  const [selectedTags, setSelectedTags] = useState<Map<string, string>>(
    new Map(),
  )
  const [isSelectingAll, setIsSelectingAll] = useState(false)
  const [selectedTrashIds, setSelectedTrashIds] = useState<Set<string>>(
    new Set(),
  )
  const [trashSearch, setTrashSearch] = useState("")
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false)
  const [confirmBulkPermanentDelete, setConfirmBulkPermanentDelete] =
    useState(false)
  const [confirmEmptyTrash, setConfirmEmptyTrash] = useState(false)
  const [emptyingTrash, setEmptyingTrash] = useState(false)
  const [showTrash, setShowTrash] = useState(false)
  const [confirmPermanentDelete, setConfirmPermanentDelete] = useState<
    string | null
  >(null)

  // Fetch tags (server-side pagination)
  const {
    data: tagsResult,
    isLoading: tagsLoading,
    error: tagsError,
  } = useQuery({
    queryKey: [
      "tags",
      currentOrganization?.id,
      tagPage,
      tagPageSize,
      appliedTagSearch,
      typeFilter,
      stateFilter,
      readerFilter,
      sortBy,
      sortOrder,
    ],
    queryFn: async () => {
      if (!currentOrganization) return { data: [], count: 0 }
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const params = new URLSearchParams({
        skip: String((tagPage - 1) * tagPageSize),
        limit: String(tagPageSize),
      })
      if (appliedTagSearch) params.set("search", appliedTagSearch)
      if (typeFilter) params.set("tag_type", typeFilter)
      if (stateFilter) params.set("state", stateFilter)
      if (readerFilter) params.set("reader_id", readerFilter)
      if (sortBy) {
        params.set("sort_by", sortBy)
        params.set("sort_order", sortOrder)
      }
      const url = isAllOrgs(currentOrganization)
        ? `${base}/api/v1/tags/paginated?${params}`
        : `${base}/api/v1/organizations/${currentOrganization.id}/tags/paginated?${params}`
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return { data: [], count: 0 }
      return res.json() as Promise<{ data: TagRead[]; count: number }>
    },
    enabled: !!currentOrganization,
  })

  const tags = tagsResult?.data ?? []
  const tagsCount = tagsResult?.count ?? 0

  // Fetch readers to map IDs to names
  const { data: readers = [] } = useQuery({
    queryKey: ["readers", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) return []
      try {
        const response = await ReadersService.readOrganizationReaders({
          organisationId: currentOrganization.id,
          skip: 0,
          limit: 100,
        })
        return response
      } catch (error) {
        return []
      }
    },
    enabled: !!currentOrganization,
  })

  const deleteTagMutation = useMutation({
    mutationFn: (id: string) =>
      TagsService.deleteOrganizationTag({
        organisationId: currentOrganization!.id,
        id: id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] })
      showToast.showSuccessToast(t("tags:tagDeleted"))
    },
    onError: (err: ApiError) => {
      const errDetail =
        (err.body as { detail?: string })?.detail || t("tags:errorLoadingDesc")
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const handleDeleteTag = (tag: TagRead) => {
    setTagToDelete(tag)
  }

  const bulkDeleteMutation = useMutation({
    mutationFn: async (entries: [string, string][]) => {
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      // Group IDs by orgId (handles "all orgs" view where orgIds may differ)
      const byOrg = new Map<string, string[]>()
      for (const [id, orgId] of entries) {
        const arr = byOrg.get(orgId) ?? []
        arr.push(id)
        byOrg.set(orgId, arr)
      }
      const results = await Promise.allSettled(
        [...byOrg.entries()].map(([orgId, ids]) =>
          fetch(`${base}/api/v1/organizations/${orgId}/tags/bulk-delete`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(ids),
          }),
        ),
      )
      const failed = results.filter((r) => r.status === "rejected").length
      if (failed > 0) throw new Error(`${failed} org group(s) failed`)
    },
    onSuccess: (_, entries) => {
      const count = entries.length
      queryClient.invalidateQueries({ queryKey: ["tags"] })
      setSelectedTags(new Map())
      showToast.showSuccessToast(`${count} tag${count > 1 ? "s" : ""} deleted.`)
    },
    onError: (err: Error) => {
      queryClient.invalidateQueries({ queryKey: ["tags"] })
      setSelectedTags(new Map())
      showToast.showErrorToast(err.message)
    },
  })

  const orgId = isAllOrgs(currentOrganization!) ? null : currentOrganization!.id

  const { data: trashResult, refetch: refetchTrash } = useQuery({
    queryKey: ["tags-trash", orgId],
    queryFn: async () => {
      if (!orgId) return { data: [], count: 0 }
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const res = await fetch(
        `${base}/api/v1/organizations/${orgId}/tags/trash?limit=200`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) return { data: [], count: 0 }
      return res.json() as Promise<{ data: TagRead[]; count: number }>
    },
    enabled: showTrash && !!orgId,
  })

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const res = await fetch(
        `${base}/api/v1/organizations/${orgId}/tags/${id}/restore`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) throw new Error("Restore failed")
    },
    onSuccess: () => {
      refetchTrash()
      showToast.showSuccessToast("Tag restored successfully.")
    },
    onError: () => showToast.showErrorToast("Failed to restore tag."),
  })

  const permanentDeleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const res = await fetch(
        `${base}/api/v1/organizations/${orgId}/tags/${id}/permanent`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) throw new Error("Permanent delete failed")
    },
    onSuccess: () => {
      refetchTrash()
      setConfirmPermanentDelete(null)
      showToast.showSuccessToast("Tag permanently deleted.")
    },
    onError: () =>
      showToast.showErrorToast("Failed to permanently delete tag."),
  })

  const bulkRestoreMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const res = await fetch(
        `${base}/api/v1/organizations/${orgId}/tags/bulk-restore`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(ids),
        },
      )
      if (!res.ok) throw new Error("Restore failed")
    },
    onSuccess: (_, ids) => {
      refetchTrash()
      setSelectedTrashIds(new Set())
      showToast.showSuccessToast(
        `${ids.length} tag${ids.length > 1 ? "s" : ""} restored.`,
      )
    },
    onError: (err: Error) => showToast.showErrorToast(err.message),
  })

  const bulkPermanentDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const BATCH = 10
      let failed = 0
      for (let i = 0; i < ids.length; i += BATCH) {
        const batch = ids.slice(i, i + BATCH)
        const results = await Promise.allSettled(
          batch.map((id) =>
            fetch(
              `${base}/api/v1/organizations/${orgId}/tags/${id}/permanent`,
              {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
              },
            ),
          ),
        )
        failed += results.filter((r) => r.status === "rejected").length
      }
      if (failed > 0) throw new Error(`${failed} deletion(s) failed`)
    },
    onSuccess: (_, ids) => {
      refetchTrash()
      setSelectedTrashIds(new Set())
      setConfirmBulkPermanentDelete(false)
      showToast.showSuccessToast(
        `${ids.length} tag${ids.length > 1 ? "s" : ""} permanently deleted.`,
      )
    },
    onError: (err: Error) => showToast.showErrorToast(err.message),
  })

  const toggleTag = (tag: TagRead) => {
    setSelectedTags((prev) => {
      const next = new Map(prev)
      next.has(tag.id)
        ? next.delete(tag.id)
        : next.set(tag.id, tag.organisation_id)
      return next
    })
  }

  const visibleTagIds = tags.map((s) => s.id)
  const allVisibleSelected =
    visibleTagIds.length > 0 &&
    visibleTagIds.every((id) => selectedTags.has(id))

  const toggleAll = async () => {
    if (selectedTags.size > 0) {
      setSelectedTags(new Map())
      return
    }
    if (tagsCount <= tags.length) {
      setSelectedTags(new Map(tags.map((s) => [s.id, s.organisation_id])))
      return
    }
    setIsSelectingAll(true)
    try {
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const BATCH = 200
      const allTags: TagRead[] = []
      let skip = 0
      while (skip < tagsCount) {
        const params = new URLSearchParams({
          skip: String(skip),
          limit: String(BATCH),
        })
        if (appliedTagSearch) params.set("search", appliedTagSearch)
        if (typeFilter) params.set("tag_type", typeFilter)
        if (stateFilter) params.set("state", stateFilter)
        if (readerFilter) params.set("reader_id", readerFilter)
        const url = isAllOrgs(currentOrganization)
          ? `${base}/api/v1/tags/paginated?${params}`
          : `${base}/api/v1/organizations/${currentOrganization!.id}/tags/paginated?${params}`
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) break
        const data = (await res.json()) as { data: TagRead[]; count: number }
        allTags.push(...data.data)
        skip += BATCH
      }
      if (allTags.length > 0) {
        setSelectedTags(new Map(allTags.map((s) => [s.id, s.organisation_id])))
      }
    } finally {
      setIsSelectingAll(false)
    }
  }

  if (!currentOrganization) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("common:org.noOrg")}
        </Heading>
        <Text color={sc.textMuted}>{t("common:org.selectPrompt")}</Text>
      </VisionCard>
    )
  }

  const availableTagTypes: TagTypeEnum[] = [
    "TEMPERATURE",
    "HUMIDITY",
    "GENERIC",
  ]

  const availableStates: TagStateEnum[] = [
    "NEW",
    "SLEEP",
    "STANDBY",
    "READY",
    "ARMED",
    "LOGGING",
    "FINISHED",
    "INVALID",
    "ALERT",
  ]

  const getStateColor = (state: TagStateEnum | null | undefined) => {
    switch (state) {
      case "ALERT":
      case "INVALID":
        return "red"
      case "LOGGING":
        return "green"
      case "ARMED":
        return "blue"
      case "READY":
        return "teal"
      case "NEW":
        return "orange"
      case "FINISHED":
        return "purple"
      default:
        return "gray"
    }
  }

  const isLoading = tagsLoading
  const hasError = tagsError

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="60vh">
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  if (hasError && (hasError as ApiError).status !== 404) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("tags:errorLoading")}
        </Heading>
        <Text color={sc.textMuted}>{t("tags:errorLoadingDesc")}</Text>
      </VisionCard>
    )
  }

  const ViewToggle = ({
    mode,
    setMode,
  }: { mode: "grid" | "table"; setMode: (m: "grid" | "table") => void }) => (
    <HStack bg={sc.toggleBg} borderRadius="10px" p="2px">
      <Box
        as="button"
        p={2}
        borderRadius="8px"
        bg={mode === "grid" ? sc.toggleActiveBg : "transparent"}
        color={mode === "grid" ? sc.heading : sc.textMuted}
        _hover={{ color: sc.heading }}
        transition="all 0.2s"
        onClick={() => setMode("grid")}
        title={t("common:actions.gridView")}
      >
        <FiGrid size={18} />
      </Box>
      <Box
        as="button"
        p={2}
        borderRadius="8px"
        bg={mode === "table" ? sc.toggleActiveBg : "transparent"}
        color={mode === "table" ? sc.heading : sc.textMuted}
        _hover={{ color: sc.heading }}
        transition="all 0.2s"
        onClick={() => setMode("table")}
        title={t("common:actions.tableView")}
      >
        <FiList size={18} />
      </Box>
    </HStack>
  )

  const renderTagGridView = () => (
    <Grid
      templateColumns={{
        base: "1fr",
        sm: "repeat(2, minmax(0, 1fr))",
        md: "repeat(2, minmax(0, 1fr))",
        lg: "repeat(3, minmax(0, 1fr))",
      }}
      gap={{ base: 3, md: 4, lg: 6 }}
    >
      {tags.map((tag) => (
        <VisionCard
          key={tag.id}
          variant="gradient"
          style={{ position: "relative" }}
        >
          <Flex direction="column" h="100%">
            <Flex justify="space-between" align="flex-start" mb={4}>
              <Box
                position="absolute"
                top={3}
                left={3}
                zIndex={1}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selectedTags.has(tag.id)}
                  onChange={() => toggleTag(tag)}
                  style={{
                    width: 16,
                    height: 16,
                    cursor: "pointer",
                    accentColor: "#34a1dd",
                  }}
                />
              </Box>
              <Box flex="1" pl={6} minW={0}>
                <Heading
                  size="md"
                  color={sc.heading}
                  mb={2}
                  wordBreak="break-word"
                >
                  {tag.name}
                </Heading>
                <VStack align="start" gap={2}>
                  <HStack gap={2} align="start">
                    <FiTag
                      fontSize="14px"
                      color={sc.textMuted}
                      style={{ flexShrink: 0, marginTop: 3 }}
                    />
                    <Text
                      color={sc.textSecondary}
                      fontSize="sm"
                      wordBreak="break-word"
                    >
                      {t("tags:table.epc")}: {tag.epcid}
                    </Text>
                  </HStack>
                  {tag.template && (
                    <>
                      <HStack gap={2}>
                        <Text fontSize="lg">
                          {getTagTypeIcon(tag.template.tag_type)}
                        </Text>
                        <Badge
                          colorScheme={getTagTypeColor(tag.template.tag_type)}
                          size="sm"
                        >
                          {tag.template.tag_type}
                        </Badge>
                      </HStack>
                      <Text color={sc.textSecondary} fontSize="sm">
                        {t("tags:table.template")}: {tag.template.name}
                      </Text>
                      {tag.template.unit && (
                        <Text color={sc.textSecondary} fontSize="sm">
                          {t("tags:table.unit")}: {tag.template.unit}
                        </Text>
                      )}
                    </>
                  )}
                  {tag.chip_type && (
                    <HStack gap={2}>
                      <FiCpu fontSize="14px" color={sc.textMuted} />
                      <Text color={sc.textSecondary} fontSize="sm">
                        Tag: {tag.chip_type}
                      </Text>
                    </HStack>
                  )}
                  {tag.state && (
                    <Badge
                      colorPalette={
                        tag.state === "LOGGING"
                          ? "green"
                          : tag.state === "FINISHED"
                            ? "blue"
                            : tag.state === "ALERT"
                              ? "red"
                              : "gray"
                      }
                      size="sm"
                    >
                      {tag.state}
                    </Badge>
                  )}
                  {tag.last_lat != null && tag.last_lon != null && (
                    <HStack gap={2}>
                      <FiMapPin fontSize="14px" color={sc.textMuted} />
                      <Text color={sc.textSecondary} fontSize="sm">
                        {Number(tag.last_lat).toFixed(4)},{" "}
                        {Number(tag.last_lon).toFixed(4)}
                      </Text>
                    </HStack>
                  )}
                  {tag.last_seen_at && (
                    <HStack gap={2}>
                      <FiClock fontSize="14px" color={sc.textMuted} />
                      <Text color={sc.textSecondary} fontSize="sm">
                        {formatDate(tag.last_seen_at)}
                      </Text>
                    </HStack>
                  )}
                  {tag.last_seen_by && (
                    <HStack gap={2}>
                      <FiWifi fontSize="14px" color={sc.textMuted} />
                      <Text color={sc.textSecondary} fontSize="sm">
                        {readers.find((r) => r.id === tag.last_seen_by)?.name ||
                          t("tags:detail.unknown")}
                      </Text>
                    </HStack>
                  )}
                </VStack>
              </Box>
              <IconBox variant="glass" size="md">
                <FiCpu fontSize="20px" color={sc.iconColor} />
              </IconBox>
            </Flex>

            <Flex gap={2} mt="auto">
              <VisionButton
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate({
                    to: "/tag-detail",
                    search: { tagId: tag.id },
                  })
                }
                flex="1"
              >
                <FiInfo /> {t("common:actions.details")}
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate({
                    to: "/tag-form",
                    search: { tagId: tag.id },
                  })
                }
                flex="1"
              >
                <FiEdit /> {t("common:actions.edit")}
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                colorPalette="red"
                onClick={() => handleDeleteTag(tag)}
              >
                <FiTrash2 />
              </VisionButton>
            </Flex>
          </Flex>
        </VisionCard>
      ))}

      {tags.length === 0 && (
        <VisionCard variant="outline">
          <Flex direction="column" align="center" justify="center" py={8}>
            <IconBox variant="glass" size="lg" mb={4}>
              <FiCpu fontSize="24px" color={sc.iconColor} />
            </IconBox>
            <Heading size="md" color={sc.heading} mb={2}>
              {tagSearch ? t("tags:noMatchingTags") : t("tags:noTags")}
            </Heading>
            <Text color={sc.textMuted} textAlign="center" mb={4}>
              {tagSearch
                ? t("tags:noMatchingTagsDesc", { search: tagSearch })
                : t("tags:noTagsDesc")}
            </Text>
            {!tagSearch && (
              <VisionButton
                variant="brand"
                onClick={() =>
                  navigate({
                    to: "/tag-form",
                    search: { tagId: undefined },
                  })
                }
              >
                <FiPlus /> {t("tags:addTag")}
              </VisionButton>
            )}
          </Flex>
        </VisionCard>
      )}
    </Grid>
  )

  const renderTagTableView = () => (
    <VisionCard p={{ base: 3, md: "22px" }}>
      {tags.length === 0 ? (
        <Flex direction="column" align="center" py={10} gap={3}>
          <FiCpu fontSize="36px" color={sc.iconMuted} />
          <Text color={sc.textMuted} fontSize="sm">
            {tagSearch
              ? t("tags:noMatchingTagsDesc", { search: tagSearch })
              : t("tags:noRegistered")}
          </Text>
        </Flex>
      ) : (
        <Box overflowX="auto" maxW="100%">
          <Table.Root
            size={{ base: "sm", md: "md" }}
            style={{ tableLayout: "fixed", minWidth: "900px" }}
          >
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader style={{ width: 40, minWidth: 40 }}>
                  {isSelectingAll ? (
                    <Spinner size="xs" color={sc.spinnerColor} />
                  ) : (
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={() => {
                        void toggleAll()
                      }}
                      style={{
                        width: 16,
                        height: 16,
                        cursor: "pointer",
                        accentColor: "#34a1dd",
                      }}
                    />
                  )}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  cursor="pointer"
                  onClick={() => toggleSort("epcid")}
                  _hover={{ color: sc.heading }}
                  style={cw("epcid", 200)}
                >
                  {t("tags:table.epc")}{" "}
                  {sortBy === "epcid" && (sortOrder === "asc" ? "▲" : "▼")}
                  {rh("epcid")}
                </Table.ColumnHeader>
                {isAllOrgs(currentOrganization) && (
                  <Table.ColumnHeader
                    color={sc.tableHeaderColor}
                    style={cw("org", 140)}
                  >
                    {t("common:filter.organisation")}
                    {rh("org")}
                  </Table.ColumnHeader>
                )}
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  cursor="pointer"
                  onClick={() => toggleSort("tag_model")}
                  _hover={{ color: sc.heading }}
                  style={cw("type", 110)}
                >
                  {t("tags:table.type")}{" "}
                  {sortBy === "tag_model" && (sortOrder === "asc" ? "▲" : "▼")}
                  {rh("type")}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  cursor="pointer"
                  onClick={() => toggleSort("state")}
                  _hover={{ color: sc.heading }}
                  style={cw("state", 100)}
                >
                  {t("common:table.state")}{" "}
                  {sortBy === "state" && (sortOrder === "asc" ? "▲" : "▼")}
                  {rh("state")}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  cursor="pointer"
                  onClick={() => toggleSort("battery_status")}
                  _hover={{ color: sc.heading }}
                  style={cw("battery", 90)}
                >
                  {t("tags:table.battery")}{" "}
                  {sortBy === "battery_status" &&
                    (sortOrder === "asc" ? "▲" : "▼")}
                  {rh("battery")}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  cursor="pointer"
                  onClick={() => toggleSort("reading_count")}
                  _hover={{ color: sc.heading }}
                  style={cw("readings", 90)}
                >
                  {t("tags:table.readings")}{" "}
                  {sortBy === "reading_count" &&
                    (sortOrder === "asc" ? "▲" : "▼")}
                  {rh("readings")}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  cursor="pointer"
                  onClick={() => toggleSort("last_seen_at")}
                  _hover={{ color: sc.heading }}
                  style={cw("lastSeen", 155)}
                >
                  {t("tags:table.lastSeen")}{" "}
                  {sortBy === "last_seen_at" &&
                    (sortOrder === "asc" ? "▲" : "▼")}
                  {rh("lastSeen")}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  cursor="pointer"
                  onClick={() => toggleSort("created_at")}
                  _hover={{ color: sc.heading }}
                  style={cw("createdAt", 155)}
                >
                  {t("tags:table.createdAt")}{" "}
                  {sortBy === "created_at" && (sortOrder === "asc" ? "▲" : "▼")}
                  {rh("createdAt")}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  style={cw("alert", 75)}
                >
                  {t("tags:table.alert")}
                  {rh("alert")}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  color={sc.tableHeaderColor}
                  style={cw("actions", 80)}
                >
                  {t("common:table.actions")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {tags.map((tag) => {
                // Each tag spans 3 rows: row 1 = primary values, row 2 = the
                // secondary sub-values (TID under EPC; hh:mm am/pm under the
                // last-seen / created dates), row 3 = the tag name across all
                // columns. dataCols = every column except the rowSpanned
                // checkbox; midSpan = the empty stretch in row 2 between the
                // TID cell and the time cells.
                const allOrgs = isAllOrgs(currentOrganization)
                const dataCols = allOrgs ? 10 : 9
                const midSpan = allOrgs ? 5 : 4
                const isSelected = selectedTags.has(tag.id)
                const rowBg = isSelected ? "rgba(52,161,221,0.08)" : undefined
                const openDetail = () =>
                  navigate({ to: "/tag-detail", search: { tagId: tag.id } })
                const timeOpts = {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                } as const
                return (
                  <Fragment key={tag.id}>
                    {/* Row 1 — primary values */}
                    <Table.Row
                      cursor="pointer"
                      _hover={{ bg: sc.hoverBg }}
                      bg={rowBg}
                      onClick={openDetail}
                      css={{ "& > td": { borderBottomWidth: 0 } }}
                    >
                      <Table.Cell
                        rowSpan={3}
                        verticalAlign="top"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleTag(tag)}
                          style={{
                            width: 16,
                            height: 16,
                            cursor: "pointer",
                            accentColor: "#34a1dd",
                          }}
                        />
                      </Table.Cell>
                      <Table.Cell fontFamily="mono" fontSize="sm">
                        <Text color={sc.tableCellMuted}>{tag.epcid}</Text>
                      </Table.Cell>
                      {allOrgs && (
                        <Table.Cell>
                          <Text color={sc.tableCellMuted} fontSize="sm">
                            {organizations.find(
                              (o) => o.id === tag.organisation_id,
                            )?.name || "—"}
                          </Text>
                        </Table.Cell>
                      )}
                      <Table.Cell>
                        {tag.tag_model ? (
                          <Badge colorScheme="teal" size="sm">
                            {tag.tag_model}
                          </Badge>
                        ) : tag.tag_type ? (
                          <Badge
                            colorScheme={getTagTypeColor(tag.tag_type)}
                            size="sm"
                          >
                            {getTagTypeIcon(tag.tag_type)} {tag.tag_type}
                          </Badge>
                        ) : (
                          <Text color={sc.textSubtle} fontSize="sm">
                            —
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {tag.state ? (
                          <Badge
                            colorScheme={getStateColor(tag.state)}
                            size="sm"
                          >
                            {t(`tags:states.${tag.state}`, tag.state)}
                          </Badge>
                        ) : (
                          <Text color={sc.textSubtle} fontSize="sm">
                            —
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell color={sc.tableCellMuted} fontSize="sm">
                        {tag.battery_status != null
                          ? `${(tag.battery_status / 1000).toFixed(2)} V`
                          : "—"}
                      </Table.Cell>
                      <Table.Cell color={sc.tableCellMuted} fontSize="sm">
                        {/* Temp logs only apply to datalogger tags; other
                            tag types have no on-chip temperature history. */}
                        {(tag.tag_type ?? tag.template?.tag_type) ===
                        "TEMPERATURE_DATALOGGER" ? (
                          tag.reading_count != null ? (
                            tag.reading_count.toLocaleString()
                          ) : (
                            "0"
                          )
                        ) : (
                          <Text color={sc.textSubtle} fontSize="sm">
                            —
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell color={sc.tableCellMuted} fontSize="sm">
                        {tag.last_seen_at
                          ? formatInTimezone(
                              tag.last_seen_at,
                              displayTimezone,
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            )
                          : "—"}
                      </Table.Cell>
                      <Table.Cell color={sc.tableCellMuted} fontSize="sm">
                        {tag.created_at
                          ? formatInTimezone(tag.created_at, displayTimezone, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "—"}
                      </Table.Cell>
                      <Table.Cell>
                        {tag.alert_active ? (
                          <Badge colorScheme="red" size="sm">
                            {t("tags:table.alertActive")}
                          </Badge>
                        ) : (
                          <Text color={sc.textSubtle} fontSize="sm">
                            —
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <HStack gap={1} onClick={(e) => e.stopPropagation()}>
                          <VisionButton
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              navigate({
                                to: "/tag-form",
                                search: { tagId: tag.id },
                              })
                            }
                          >
                            <FiEdit />
                          </VisionButton>
                          <VisionButton
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTag(tag)}
                          >
                            <FiTrash2 />
                          </VisionButton>
                        </HStack>
                      </Table.Cell>
                    </Table.Row>
                    {/* Row 2 — secondary sub-values (TID + times) */}
                    <Table.Row
                      cursor="pointer"
                      _hover={{ bg: sc.hoverBg }}
                      bg={rowBg}
                      onClick={openDetail}
                      css={{
                        "& > td": { borderBottomWidth: 0, paddingTop: 0 },
                      }}
                    >
                      <Table.Cell fontFamily="mono">
                        {tag.tid ? (
                          <Text color={sc.textSubtle} fontSize="xs">
                            {tag.tid.slice(0, 24)}
                          </Text>
                        ) : null}
                      </Table.Cell>
                      <Table.Cell colSpan={midSpan} />
                      <Table.Cell color={sc.textMuted} fontSize="xs">
                        {tag.last_seen_at
                          ? formatInTimezone(
                              tag.last_seen_at,
                              displayTimezone,
                              timeOpts,
                            )
                          : null}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted} fontSize="xs">
                        {tag.created_at
                          ? formatInTimezone(
                              tag.created_at,
                              displayTimezone,
                              timeOpts,
                            )
                          : null}
                      </Table.Cell>
                      <Table.Cell colSpan={2} />
                    </Table.Row>
                    {/* Row 3 — tag name spanning all columns */}
                    <Table.Row
                      cursor="pointer"
                      _hover={{ bg: sc.hoverBg }}
                      bg={rowBg}
                      onClick={openDetail}
                    >
                      <Table.Cell colSpan={dataCols} css={{ paddingTop: 0 }}>
                        <Text
                          fontWeight="medium"
                          color={sc.heading}
                          fontSize="sm"
                        >
                          {tag.name || "—"}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  </Fragment>
                )
              })}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </VisionCard>
  )

  const renderTrashView = () => {
    const q = trashSearch.trim().toLowerCase()
    const trashed = (trashResult?.data ?? []).filter(
      (s) =>
        !q ||
        s.id.toLowerCase().includes(q) ||
        s.name.toLowerCase().includes(q) ||
        s.epcid.toLowerCase().includes(q),
    )
    const allTrashSelected =
      trashed.length > 0 && trashed.every((s) => selectedTrashIds.has(s.id))
    const toggleTrash = (id: string) =>
      setSelectedTrashIds((prev) => {
        const next = new Set(prev)
        next.has(id) ? next.delete(id) : next.add(id)
        return next
      })
    const toggleAllTrash = async () => {
      if (allTrashSelected) {
        setSelectedTrashIds(new Set())
        return
      }
      const total = trashResult?.count ?? 0
      if (total <= (trashResult?.data?.length ?? 0)) {
        setSelectedTrashIds(new Set(trashed.map((s) => s.id)))
        return
      }
      // More trash than loaded — fetch all IDs in batches
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const BATCH = 200
      const allIds = new Set<string>()
      let skip = 0
      while (skip < total) {
        const res = await fetch(
          `${base}/api/v1/organizations/${orgId}/tags/trash?limit=${BATCH}&skip=${skip}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (!res.ok) break
        const page = (await res.json()) as {
          data: { id: string }[]
          count: number
        }
        for (const s of page.data) allIds.add(s.id)
        skip += BATCH
        if (page.data.length < BATCH) break
      }
      setSelectedTrashIds(allIds)
    }

    return (
      <>
        <Box mb={3}>
          <input
            type="search"
            placeholder="Search by name, EPC or ID…"
            value={trashSearch}
            onChange={(e) => setTrashSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 12px",
              borderRadius: "8px",
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.05)",
              color: "inherit",
              fontSize: "14px",
              outline: "none",
            }}
          />
        </Box>
        {selectedTrashIds.size > 0 && (
          <Flex
            align="center"
            justify="space-between"
            px={4}
            py={2}
            mb={3}
            bg="rgba(52,161,221,0.08)"
            borderRadius="8px"
            border="1px solid rgba(52,161,221,0.3)"
          >
            <Text fontSize="sm" color="blue.300">
              {selectedTrashIds.size} tag{selectedTrashIds.size > 1 ? "s" : ""}{" "}
              selected
            </Text>
            <HStack gap={2}>
              <VisionButton
                variant="ghost"
                size="sm"
                onClick={() => setSelectedTrashIds(new Set())}
              >
                {t("common:actions.cancel")}
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                onClick={() =>
                  bulkRestoreMutation.mutate([...selectedTrashIds])
                }
                loading={bulkRestoreMutation.isPending}
              >
                <FiRotateCcw /> Restore selected
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                onClick={() => setConfirmBulkPermanentDelete(true)}
                style={{ color: "#FC8181" }}
              >
                <FiTrash2 /> Delete permanently
              </VisionButton>
            </HStack>
          </Flex>
        )}
        <Box position="relative">
          {(bulkRestoreMutation.isPending ||
            bulkPermanentDeleteMutation.isPending) && (
            <Flex
              position="absolute"
              inset={0}
              zIndex={10}
              align="center"
              justify="center"
              bg="rgba(0,0,0,0.45)"
              borderRadius="inherit"
              gap={3}
            >
              <Spinner size="md" color={sc.spinnerColor} />
              <Text fontSize="sm" color="white">
                {bulkRestoreMutation.isPending ? "Restoring…" : "Deleting…"}
              </Text>
            </Flex>
          )}
          <VisionCard>
            {trashed.length === 0 ? (
              <Flex direction="column" align="center" py={10} gap={3}>
                <FiTrash2 fontSize="36px" color={sc.iconMuted} />
                <Text color={sc.textMuted} fontSize="sm">
                  Trash is empty — no deleted tags here.
                </Text>
              </Flex>
            ) : (
              <Box overflowX="auto" maxW="100%">
                <Table.Root
                  size={{ base: "sm", md: "md" }}
                  style={{ minWidth: "700px" }}
                >
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader w="40px">
                        <input
                          type="checkbox"
                          checked={allTrashSelected}
                          onChange={toggleAllTrash}
                          style={{
                            width: 16,
                            height: 16,
                            cursor: "pointer",
                            accentColor: "#34a1dd",
                          }}
                        />
                      </Table.ColumnHeader>
                      <Table.ColumnHeader color={sc.tableHeaderColor}>
                        {t("tags:table.epc")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader color={sc.tableHeaderColor}>
                        {t("common:table.name")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader color={sc.tableHeaderColor}>
                        {t("common:table.state")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader color={sc.tableHeaderColor}>
                        {t("common:table.actions")}
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {trashed.map((tag) => (
                      <Table.Row
                        key={tag.id}
                        _hover={{ bg: sc.hoverBg }}
                        bg={
                          selectedTrashIds.has(tag.id)
                            ? "rgba(52,161,221,0.08)"
                            : undefined
                        }
                      >
                        <Table.Cell onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedTrashIds.has(tag.id)}
                            onChange={() => toggleTrash(tag.id)}
                            style={{
                              width: 16,
                              height: 16,
                              cursor: "pointer",
                              accentColor: "#34a1dd",
                            }}
                          />
                        </Table.Cell>
                        <Table.Cell
                          fontFamily="mono"
                          fontSize="sm"
                          color={sc.tableCellMuted}
                        >
                          {tag.epcid}
                        </Table.Cell>
                        <Table.Cell color={sc.tableCellMuted} fontSize="sm">
                          {tag.name}
                        </Table.Cell>
                        <Table.Cell>
                          {tag.state ? (
                            <Badge
                              colorScheme={getStateColor(tag.state)}
                              size="sm"
                            >
                              {tag.state}
                            </Badge>
                          ) : (
                            <Text color={sc.textSubtle} fontSize="sm">
                              —
                            </Text>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <HStack gap={2}>
                            <VisionButton
                              variant="ghost"
                              size="sm"
                              onClick={() => restoreMutation.mutate(tag.id)}
                              loading={restoreMutation.isPending}
                            >
                              <FiRotateCcw /> Restore
                            </VisionButton>
                            <VisionButton
                              variant="ghost"
                              size="sm"
                              onClick={() => setConfirmPermanentDelete(tag.id)}
                              style={{ color: "#FC8181" }}
                            >
                              <FiTrash2 /> Delete permanently
                            </VisionButton>
                          </HStack>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
          </VisionCard>
        </Box>
      </>
    )
  }

  return (
    <Box>
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        gap={{ base: 3, md: 0 }}
        mb={{ base: 5, md: 8 }}
      >
        <Box>
          <Heading size={{ base: "lg", md: "xl" }} color={sc.heading} mb={2}>
            {t("tags:title")}
          </Heading>
          <Text color={sc.textMuted} fontSize={{ base: "sm", md: "md" }}>
            {t("tags:subtitle", { orgName: currentOrganization.name })}
          </Text>
        </Box>
        <Flex gap={2} wrap="wrap" align="center">
          {!showTrash && (
            <>
              <HStack
                gap={2}
                bg={sc.toggleBg}
                borderRadius="10px"
                px={3}
                py="2px"
              >
                {isSelectingAll ? (
                  <Spinner size="xs" color={sc.spinnerColor} />
                ) : (
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={() => {
                      void toggleAll()
                    }}
                    style={{
                      width: 16,
                      height: 16,
                      cursor: "pointer",
                      accentColor: "#34a1dd",
                    }}
                  />
                )}
                <Text fontSize="xs" color={sc.textMuted}>
                  {t("common:actions.selectAll")}
                </Text>
              </HStack>
              <TimezoneSelector
                value={displayTimezone}
                onChange={(tz) => {
                  setDisplayTimezone(tz)
                  localStorage.setItem("tags-timezone", tz)
                }}
              />
              <Box>
                <ViewToggle mode={tagViewMode} setMode={updateTagViewMode} />
              </Box>
            </>
          )}
          {orgId && (
            <VisionButton
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowTrash(!showTrash)
                setSelectedTrashIds(new Set())
                setTrashSearch("")
              }}
            >
              {showTrash ? (
                <>
                  <FiX /> Back to Tags
                </>
              ) : (
                <>
                  <FiTrash2 /> Trash
                </>
              )}
            </VisionButton>
          )}
          {orgId && (trashResult?.count ?? 0) > 0 && (
            <VisionButton
              variant="ghost"
              size="sm"
              colorPalette="red"
              onClick={() => {
                if (demoBlocksEmptyTrash) {
                  showToast.showErrorToast(
                    t(
                      "tags:trash.emptyTrashDemoBlocked",
                      "Emptying the trash is disabled in demo mode. Sign in as an administrator to permanently delete tags.",
                    ),
                  )
                  return
                }
                setConfirmEmptyTrash(true)
              }}
              style={{
                color: demoBlocksEmptyTrash ? "#a0aec0" : "#f56565",
                borderColor: demoBlocksEmptyTrash
                  ? "rgba(160,174,192,0.3)"
                  : "rgba(245,101,101,0.4)",
                opacity: demoBlocksEmptyTrash ? 0.7 : 1,
              }}
              title={
                demoBlocksEmptyTrash
                  ? t(
                      "tags:trash.emptyTrashDemoTooltip",
                      "Restricted in demo mode — sign in as an administrator to permanently delete tags",
                    )
                  : t(
                      "tags:trash.emptyTrashHint",
                      "Permanently delete every tag in trash",
                    )
              }
            >
              <FiTrash2 /> {t("tags:trash.emptyTrash", "Empty trash")}
            </VisionButton>
          )}
          {!showTrash && (
            <VisionButton
              variant="brand"
              onClick={() =>
                navigate({
                  to: "/tag-form",
                  search: { tagId: undefined },
                })
              }
            >
              <FiPlus />
              {t("tags:addTag")}
            </VisionButton>
          )}
        </Flex>
      </Flex>

      {showTrash ? (
        renderTrashView()
      ) : (
        <>
          <VisionCard mb={6}>
            <Flex
              direction={{ base: "column", sm: "row" }}
              gap={2}
              align={{ base: "stretch", sm: "center" }}
            >
              <InputGroup
                startElement={<FiSearch color={sc.iconMuted} />}
                flex={1}
              >
                <VisionInput
                  placeholder={t("tags:searchTags")}
                  value={tagSearch}
                  onChange={(e) => setTagSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") applyTagFilter()
                  }}
                />
              </InputGroup>
              <Flex gap={2} wrap="wrap">
                <VisionButton
                  variant="brand"
                  onClick={applyTagFilter}
                  size="sm"
                >
                  {t("common:actions.filter")}
                </VisionButton>
                {(appliedTagSearch ||
                  typeFilter ||
                  stateFilter ||
                  readerFilter) && (
                  <VisionButton
                    variant="ghost"
                    onClick={clearTagFilter}
                    size="sm"
                  >
                    <FiX /> {t("common:actions.clearFilter")}
                  </VisionButton>
                )}
              </Flex>
            </Flex>
            <Grid
              templateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }}
              gap={3}
              mt={3}
            >
              <Box>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  fontWeight="bold"
                  mb={1}
                >
                  {t("common:filter.type")}
                </Text>
                <VisionSelect
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter((e.target as HTMLSelectElement).value)
                    setTagPage(1)
                  }}
                >
                  <option value="">{t("common:filter.all")}</option>
                  {availableTagTypes.map((type) => (
                    <option key={type} value={type}>
                      {t(`tags:types.${type}`, type)}
                    </option>
                  ))}
                </VisionSelect>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  fontWeight="bold"
                  mb={1}
                >
                  {t("common:filter.state")}
                </Text>
                <VisionSelect
                  value={stateFilter}
                  onChange={(e) => {
                    setStateFilter((e.target as HTMLSelectElement).value)
                    setTagPage(1)
                  }}
                >
                  <option value="">{t("common:filter.all")}</option>
                  {availableStates.map((state) => (
                    <option key={state} value={state}>
                      {t(`tags:states.${state}`, state)}
                    </option>
                  ))}
                </VisionSelect>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  fontWeight="bold"
                  mb={1}
                >
                  {t("common:filter.reader")}
                </Text>
                <VisionSelect
                  value={readerFilter}
                  onChange={(e) => {
                    setReaderFilter((e.target as HTMLSelectElement).value)
                    setTagPage(1)
                  }}
                >
                  <option value="">{t("common:filter.all")}</option>
                  {readers.map((reader) => (
                    <option key={reader.id} value={reader.id}>
                      {reader.name}
                    </option>
                  ))}
                </VisionSelect>
              </Box>
            </Grid>
          </VisionCard>

          {selectedTags.size > 0 && (
            <Flex
              align="center"
              justify="space-between"
              px={4}
              py={2}
              mb={4}
              borderRadius="8px"
              bg="rgba(252,129,129,0.08)"
              border="1px solid rgba(252,129,129,0.3)"
            >
              <HStack gap={2}>
                <Text fontSize="sm" color="red.300">
                  {selectedTags.size} tag{selectedTags.size > 1 ? "s" : ""}{" "}
                  selected
                </Text>
                {selectedTags.size === tagsCount && tagsCount > tagPageSize && (
                  <Text fontSize="xs" color="red.200">
                    (all {tagsCount})
                  </Text>
                )}
              </HStack>
              <HStack gap={2}>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedTags(new Map())}
                >
                  {t("common:actions.cancel")}
                </VisionButton>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmBulkDelete(true)}
                  style={{
                    borderColor: "rgba(255,100,100,0.4)",
                    color: "#FC8181",
                  }}
                >
                  <FiTrash2 /> {t("common:actions.deleteSelected")}
                </VisionButton>
              </HStack>
            </Flex>
          )}

          <Box position="relative">
            {bulkDeleteMutation.isPending && (
              <Flex
                position="absolute"
                inset={0}
                zIndex={10}
                align="center"
                justify="center"
                bg="rgba(0,0,0,0.45)"
                borderRadius="12px"
                gap={3}
              >
                <Spinner size="md" color={sc.spinnerColor} />
                <Text fontSize="sm" color="white">
                  Deleting…
                </Text>
              </Flex>
            )}
            {effectiveViewMode === "grid"
              ? renderTagGridView()
              : renderTagTableView()}
          </Box>

          <Flex
            mt={4}
            direction={{ base: "column", md: "row" }}
            alignItems={{ base: "stretch", md: "center" }}
            justifyContent="space-between"
            gap={{ base: 3, md: 4 }}
          >
            <Flex gap={2} wrap="wrap" align="center">
              <Text fontSize="sm" color={sc.textMuted}>
                {t("tags:table.totalTags", { count: tagsCount })}
              </Text>
              <HStack gap={1} flexWrap="wrap">
                <Text fontSize="sm" color={sc.textMuted}>
                  {t("common:perPage")}:
                </Text>
                {[50, 100, 200, 500].map((n) => (
                  <button
                    key={n}
                    onClick={() => {
                      setTagPageSize(n)
                      localStorage.setItem("tags-page-size", String(n))
                      setTagPage(1)
                    }}
                    style={{
                      padding: "2px 8px",
                      borderRadius: 6,
                      fontSize: "0.75rem",
                      cursor: "pointer",
                      border:
                        tagPageSize === n
                          ? "1px solid #34a1dd"
                          : "1px solid transparent",
                      background:
                        tagPageSize === n
                          ? "rgba(52,161,221,0.15)"
                          : "transparent",
                      color: tagPageSize === n ? "#34a1dd" : "inherit",
                    }}
                  >
                    {n}
                  </button>
                ))}
              </HStack>
            </Flex>
            {tagsCount > tagPageSize && (
              <Box overflowX="auto" maxW="100%">
                <PaginationRoot
                  page={tagPage}
                  count={tagsCount}
                  pageSize={tagPageSize}
                  onPageChange={(e) => setTagPage(e.page)}
                >
                  <HStack flexWrap="wrap">
                    <PaginationPrevTrigger />
                    <PaginationItems />
                    <PaginationNextTrigger />
                  </HStack>
                </PaginationRoot>
              </Box>
            )}
          </Flex>
        </>
      )}

      <ConfirmDialog
        open={!!tagToDelete}
        onClose={() => setTagToDelete(null)}
        onConfirm={() => {
          if (tagToDelete) deleteTagMutation.mutate(tagToDelete.id)
        }}
        title={t("common:confirm.deleteTitle", {
          type: t("tags:tagTags"),
        })}
        message={t("tags:confirmDeleteTag", {
          name: tagToDelete?.name,
        })}
        loading={deleteTagMutation.isPending}
      />

      <ConfirmDialog
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        onConfirm={() => bulkDeleteMutation.mutate([...selectedTags.entries()])}
        title={`Delete ${selectedTags.size} tag${selectedTags.size > 1 ? "s" : ""}`}
        message={`Are you sure you want to delete ${selectedTags.size} tag${selectedTags.size > 1 ? "s" : ""}? This cannot be undone.`}
        loading={bulkDeleteMutation.isPending}
      />

      <ConfirmDialog
        open={!!confirmPermanentDelete}
        onClose={() => setConfirmPermanentDelete(null)}
        onConfirm={() => {
          if (confirmPermanentDelete)
            permanentDeleteMutation.mutate(confirmPermanentDelete)
        }}
        title="Delete permanently"
        message="This will permanently delete the tag and all its historical readings. This cannot be undone."
        loading={permanentDeleteMutation.isPending}
      />

      <ConfirmDialog
        open={confirmBulkPermanentDelete}
        onClose={() => setConfirmBulkPermanentDelete(false)}
        onConfirm={() =>
          bulkPermanentDeleteMutation.mutate([...selectedTrashIds])
        }
        title={`Delete permanently ${selectedTrashIds.size} tag${selectedTrashIds.size > 1 ? "s" : ""}`}
        message={`This will permanently delete ${selectedTrashIds.size} tag${selectedTrashIds.size > 1 ? "s" : ""} and all their historical readings. This cannot be undone.`}
        loading={bulkPermanentDeleteMutation.isPending}
      />

      <ConfirmDialog
        open={confirmEmptyTrash}
        onClose={() => !emptyingTrash && setConfirmEmptyTrash(false)}
        onConfirm={async () => {
          if (!orgId) return
          setEmptyingTrash(true)
          try {
            // Fetch every trash ID across pages, then bulk-delete.
            const token = localStorage.getItem("access_token")
            const base = (OpenAPI.BASE || "").replace(/\/$/, "")
            const BATCH = 200
            const allIds: string[] = []
            let skip = 0
            const total = trashResult?.count ?? 0
            while (skip < total) {
              const res = await fetch(
                `${base}/api/v1/organizations/${orgId}/tags/trash?limit=${BATCH}&skip=${skip}`,
                { headers: { Authorization: `Bearer ${token}` } },
              )
              if (!res.ok) break
              const page = (await res.json()) as { data: { id: string }[] }
              for (const s of page.data) allIds.push(s.id)
              skip += BATCH
              if (page.data.length < BATCH) break
            }
            if (allIds.length === 0) {
              showToast.showSuccessToast("Trash is already empty.")
              setConfirmEmptyTrash(false)
              return
            }
            await bulkPermanentDeleteMutation.mutateAsync(allIds)
          } catch (err) {
            showToast.showErrorToast(
              err instanceof Error ? err.message : "Failed to empty trash.",
            )
          } finally {
            setEmptyingTrash(false)
            setConfirmEmptyTrash(false)
          }
        }}
        title={t("tags:trash.emptyTrashConfirmTitle", {
          count: trashResult?.count ?? 0,
        })}
        message={t("tags:trash.emptyTrashConfirmMessage", {
          count: trashResult?.count ?? 0,
        })}
        loading={emptyingTrash || bulkPermanentDeleteMutation.isPending}
      />
    </Box>
  )
}
